"use client";

/**
 * ImageLibraryPanel.tsx
 *
 * Displays project image assets using lightweight thumbnails.
 * Original signed URLs are fetched only when an asset is inserted or previewed.
 */

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocale, useTranslations } from "next-intl";
import { useEditorStore } from "./editorStore";
import { printApiDelete, printApiGet, printApiPost } from "./printApi";

type AssetRow = {
  id: number | string;
  original_name: string | null;
  mime_type: string | null;
  width_px: number | string | null;
  height_px: number | string | null;
  created_at: string;
};

type AssetItem = AssetRow & {
  id: number;
  width_px: number | null;
  height_px: number | null;
  download_url?: string | null;
  thumbnail_url?: string | null;
};

const apiGet = printApiGet;
const apiPost = printApiPost;
const apiDelete = printApiDelete;

function normalizeAssetRow(asset: AssetRow): AssetItem {
  const width = Number(asset.width_px || 0);
  const height = Number(asset.height_px || 0);
  return {
    ...asset,
    id: Number(asset.id),
    width_px: Number.isFinite(width) && width > 0 ? width : null,
    height_px: Number.isFinite(height) && height > 0 ? height : null
  };
}

function collectUsedAssetIds(...docs: any[]) {
  const out = new Set<number>();
  for (const doc of docs) {
    const pages = Array.isArray(doc?.pages) ? doc.pages : [];
    for (const page of pages) {
      const elements = Array.isArray(page?.elements) ? page.elements : [];
      for (const el of elements) {
        if (el?.type !== "image" && el?.type !== "background" && !el?.is_background) continue;
        const assetId = Number(el.asset_id || 0);
        if (assetId > 0) out.add(assetId);
      }
    }
  }
  return out;
}

function MagnifierIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="2" />
      <path d="m16 16 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function RemoveIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 7h12M10 11v6M14 11v6M9 7l1-3h4l1 3M8 7l1 13h6l1-13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function ImageLibraryPanel({
  onInsertAsset,
  onInserted,
  refreshNonce = 0,
  readOnly = false,
  onAssetDragStart,
  onAssetDragEnd,
}: {
  onInsertAsset?: (asset: AssetItem) => Promise<void> | void;
  onInserted?: () => void;
  refreshNonce?: number;
  readOnly?: boolean;
  onAssetDragStart?: () => void;
  onAssetDragEnd?: () => void;
}) {
  const t = useTranslations("editor.library");
  const locale = useLocale();
  const store = useEditorStore();
  const projectId = store.projectId;
  const usedAssetIds = useMemo(
    () => collectUsedAssetIds(store.contentData, store.coverData),
    [store.contentData, store.coverData]
  );

  const [items, setItems] = useState<AssetItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hideUsed, setHideUsed] = useState(true);
  const [preview, setPreview] = useState<{
    asset: AssetItem;
    imageUrl?: string | null;
    loading?: boolean;
    error?: string | null;
  } | null>(null);
  const [dragGhost, setDragGhost] = useState<null | { asset: AssetItem; x: number; y: number }>(null);
  const suppressNextClickRef = React.useRef(false);

  async function refresh() {
    if (!projectId) return;

    try {
      setLoading(true);
      setErr(null);

      const assets = (await apiGet<AssetRow[]>(`projects/${projectId}/assets`)).map(normalizeAssetRow);
      if (assets.length === 0) {
        setItems([]);
        return;
      }

      const batch = await apiPost<{ items: Array<{ asset_id: number | string; thumbnail_url?: string | null; download_url?: string | null }> }>(
        "assets/download-urls",
        { asset_ids: assets.map((a) => a.id), kind: "thumbnail" }
      );

      const map = new Map<number, string>();
      for (const it of batch.items || []) {
        const assetId = Number(it.asset_id || 0);
        const url = it.thumbnail_url || it.download_url || "";
        if (assetId > 0 && url) map.set(assetId, url);
      }

      setItems(assets.map((a) => ({ ...a, thumbnail_url: map.get(a.id) || null })));
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!projectId) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, refreshNonce]);

  async function getOriginalUrl(asset: AssetItem) {
    const dl = await apiGet<{ download_url: string }>(`assets/${asset.id}/download-url`);
    const next = dl.download_url || null;
    setItems((prev) => prev.map((item) => (item.id === asset.id ? { ...item, download_url: next } : item)));
    return next;
  }

  async function insert(asset: AssetItem) {
    if (readOnly) return;
    const downloadUrl = await getOriginalUrl(asset);
    const withUrl = { ...asset, download_url: downloadUrl };

    if (onInsertAsset) {
      await onInsertAsset(withUrl);
      setPreview(null);
      onInserted?.();
      return;
    }

    const doc = store.activeDoc as "content" | "cover";
    const docData = doc === "content" ? store.contentData : store.coverData;
    const setDocData = doc === "content" ? store.setContentData : store.setCoverData;
    const pageIndex = doc === "content" ? store.contentUnitIndex : store.coverPageIndex;
    if (!docData) return;

    const pages = Array.isArray(docData.pages) ? [...docData.pages] : [];
    const page = pages[pageIndex] || { elements: [] };
    const els = Array.isArray(page.elements) ? [...page.elements] : [];
    const id = `el_${Math.random().toString(16).slice(2)}`;

    els.push({
      id,
      type: "image",
      source: "asset",
      asset_id: asset.id,
      image_url: downloadUrl,
      image_mode: "free",
      x_mm: 20,
      y_mm: 120 + els.length * 4,
      w_mm: 80,
      h_mm: 60,
      fit: "cover",
      scale: 1,
      opacity: 1,
      rotate_deg: 0,
      pan_mm: { x: 0, y: 0 }
    });

    pages[pageIndex] = { ...page, elements: els };
    setDocData({ ...docData, pages });
    store.selectElement(doc, id);
    setPreview(null);
    onInserted?.();
  }

  async function openPreview(asset: AssetItem) {
    setPreview({ asset, imageUrl: asset.download_url || null, loading: true, error: null });
    try {
      const imageUrl = await getOriginalUrl(asset);
      setPreview({ asset: { ...asset, download_url: imageUrl }, imageUrl, loading: false, error: null });
    } catch (e: any) {
      setPreview({ asset, imageUrl: asset.thumbnail_url || null, loading: false, error: String(e?.message || e) });
    }
  }

  async function removeAsset(asset: AssetItem) {
    if (readOnly) return;
    const isUsed = usedAssetIds.has(asset.id);
    const confirmed = window.confirm(
      isUsed
        ? locale === "pl"
          ? "To zdjecie jest uzyte w projekcie. Usuniecie z biblioteki moze uszkodzic podglad i eksport. Usunac?"
          : "This image is used in the project. Removing it from the library can break preview and export. Delete it?"
        : locale === "pl"
          ? "Usunac zdjecie z biblioteki?"
          : "Delete this image from the library?"
    );
    if (!confirmed) return;

    try {
      await apiDelete<{ ok: boolean }>(`assets/${asset.id}`);
      setItems((prev) => prev.filter((item) => item.id !== asset.id));
    } catch (e: any) {
      setErr(String(e?.message || e));
    }
  }

  function beginPointerDrag(asset: AssetItem, event: React.PointerEvent) {
    if (readOnly || event.button !== 0) return;
    const startX = event.clientX;
    const startY = event.clientY;
    let didDrag = false;

    const onMove = (moveEvent: PointerEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      if (!didDrag && Math.hypot(dx, dy) < 6) return;

      if (!didDrag) {
        didDrag = true;
        suppressNextClickRef.current = true;
        onAssetDragStart?.();
      }
      setDragGhost({ asset, x: moveEvent.clientX, y: moveEvent.clientY });
    };

    const onUp = (upEvent: PointerEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);

      if (didDrag) {
        window.dispatchEvent(
          new CustomEvent("editor:asset-pointer-drop", {
            detail: {
              asset_id: asset.id,
              width_px: asset.width_px || undefined,
              height_px: asset.height_px || undefined,
              client_x: upEvent.clientX,
              client_y: upEvent.clientY,
            },
          })
        );
        window.setTimeout(() => {
          suppressNextClickRef.current = false;
        }, 0);
      }

      setDragGhost(null);
      onAssetDragEnd?.();
    };

    const onCancel = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      setDragGhost(null);
      onAssetDragEnd?.();
      window.setTimeout(() => {
        suppressNextClickRef.current = false;
      }, 0);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
  }

  const visibleItems = hideUsed ? items.filter((item) => !usedAssetIds.has(item.id)) : items;
  const text = {
    hideUsed: locale === "pl" ? "Ukryj uzyte zdjecia" : "Hide used images",
    preview: locale === "pl" ? "Podglad zdjecia" : "Image preview",
    insert: locale === "pl" ? "Wstaw do projektu" : "Insert into project",
    remove: locale === "pl" ? "Usun" : "Remove",
    used: locale === "pl" ? "Uzyte" : "Used",
    clickToInsert: locale === "pl" ? "Kliknij, aby wstawic" : "Click to insert",
    dragToPlace: locale === "pl" ? "Przeciagnij na strone" : "Drag onto page",
    empty: locale === "pl" ? "Brak zdjec w bibliotece." : "No images in the library.",
  };

  const previewPortal =
    preview && typeof document !== "undefined"
      ? createPortal(
          <div className="fixed inset-0 z-[20000] bg-black/88 p-4 text-white">
            <button
              type="button"
              className="absolute right-5 top-5 rounded-md border border-white/20 bg-black/50 px-3 py-2 text-sm hover:bg-white/10"
              onClick={() => setPreview(null)}
            >
              X
            </button>
            <div className="flex h-full flex-col items-center justify-center gap-4">
              <div className="max-w-[90vw] text-center text-sm font-semibold text-white/90">
                {preview.asset.original_name || `asset-${preview.asset.id}`}
              </div>
              <div className="flex min-h-0 w-full flex-1 items-center justify-center">
                {preview.loading ? (
                  <div className="text-sm text-white/70">{t("loading")}</div>
                ) : preview.imageUrl ? (
                  <img
                    src={preview.imageUrl}
                    alt=""
                    className="max-h-full max-w-full object-contain shadow-2xl"
                  />
                ) : (
                  <div className="text-sm text-white/70">{preview.error || t("projectNotReady")}</div>
                )}
              </div>
              <button
                type="button"
                className="rounded-lg bg-white px-5 py-3 text-sm font-semibold text-black shadow-lg hover:bg-slate-100 disabled:opacity-50"
                onClick={() => insert(preview.asset)}
                disabled={readOnly || preview.loading}
              >
                {text.insert}
              </button>
            </div>
          </div>,
          document.body
        )
      : null;
  const dragPortal =
    dragGhost && typeof document !== "undefined"
      ? createPortal(
          <div
            className="pointer-events-none fixed z-[21000] h-24 w-24 overflow-hidden rounded-lg border border-white/30 bg-black/40 shadow-2xl"
            style={{ left: dragGhost.x + 14, top: dragGhost.y + 14 }}
          >
            {dragGhost.asset.thumbnail_url ? (
              <img src={dragGhost.asset.thumbnail_url} alt="" className="h-full w-full object-cover opacity-90" />
            ) : null}
          </div>,
          document.body
        )
      : null;

  return (
    <aside className="space-y-3 rounded-xl">
      <div className="flex items-center justify-between gap-3">
        <label className="flex min-w-0 items-center gap-2 text-xs font-medium text-[#cbd5e1]">
          <input
            type="checkbox"
            checked={hideUsed}
            onChange={(event) => setHideUsed(event.target.checked)}
            className="h-4 w-4 accent-white"
          />
          <span className="truncate">{text.hideUsed}</span>
        </label>
        <button
          className="rounded-lg border border-white/10 bg-white/6 px-3 py-1 text-xs text-white transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={refresh}
          disabled={!projectId || loading}
          type="button"
        >
          {loading ? t("loading") : t("refresh")}
        </button>
      </div>

      {!projectId ? (
        <div className="text-sm text-gray-500">{t("projectNotReady")}</div>
      ) : null}

      {err ? (
        <pre className="rounded bg-red-50 p-2 text-xs text-red-700">{err}</pre>
      ) : null}

      <div className="grid max-h-[65vh] grid-cols-2 gap-3 overflow-auto pb-5">
        {visibleItems.map((a) => {
          const isUsed = usedAssetIds.has(a.id);
          return (
            <div
              key={a.id}
              onPointerDown={(event) => beginPointerDrag(a, event)}
              className={`group relative overflow-hidden rounded-lg border bg-white/5 text-white ${
                isUsed ? "border-emerald-300/35" : "border-white/10"
              }`}
            >
              <button
                type="button"
                className="block w-full text-left"
                onClick={() => {
                  if (suppressNextClickRef.current) return;
                  insert(a);
                }}
                disabled={readOnly}
                title={`${text.clickToInsert}. ${text.dragToPlace}.`}
              >
                <div
                  className="cursor-grab active:cursor-grabbing"
                >
                  {a.thumbnail_url ? (
                    <img
                      src={a.thumbnail_url}
                      alt=""
                      draggable={false}
                      className="h-32 w-full bg-black/20 object-cover"
                    />
                  ) : (
                    <div className="h-32 w-full bg-white/10" />
                  )}
                </div>
                <div className="min-h-12 px-2 py-2">
                  <div className="truncate text-[11px] font-semibold text-white/85">
                    {a.original_name || `asset-${a.id}`}
                  </div>
                  <div className="mt-0.5 truncate text-[10px] text-[#98a4bc]">
                    {a.width_px && a.height_px ? `${a.width_px} x ${a.height_px}px` : text.dragToPlace}
                  </div>
                </div>
              </button>

              {isUsed ? (
                <div className="absolute left-2 top-2 rounded bg-emerald-500/90 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  {text.used}
                </div>
              ) : null}

              <div className="absolute right-2 top-2 flex gap-1 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                <button
                  type="button"
                  className="rounded-md border border-white/15 bg-black/60 p-1.5 text-white shadow-sm hover:bg-white/20"
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    openPreview(a);
                  }}
                  title={text.preview}
                  aria-label={text.preview}
                >
                  <MagnifierIcon />
                </button>
                <button
                  type="button"
                  className="rounded-md border border-white/15 bg-black/60 p-1.5 text-white shadow-sm hover:bg-red-600"
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    removeAsset(a);
                  }}
                  disabled={readOnly}
                  title={text.remove}
                  aria-label={text.remove}
                >
                  <RemoveIcon />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {!loading && visibleItems.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-4 text-center text-xs text-[#98a4bc]">
          {text.empty}
        </div>
      ) : null}

      {previewPortal}
      {dragPortal}
    </aside>
  );
}
